/**
 * Where a hologram's labels go (D174): each room's name placed where it
 * covers nothing — on the top face when it fits there, beside the box when
 * it does not, above it on a leader when neither side is clear.
 *
 * @remarks
 * D169 centered every label on its box's top face, which is right for a
 * chamber with a short name and wrong for everything else: a corridor's
 * face is forty pixels wide, a name is a hundred, and a chain of rooms
 * along one axis projects each box sixty pixels across and thirty down
 * from the last — so the names ran over the next box's faces and each
 * other (Mike, on the demo, 2026-09-12). The remedy is the one every
 * map has: a label is a thing with a size, and it goes where nothing else
 * is.
 *
 * **What is known here.** The text's size is estimated, not measured —
 * the engine is isomorphic and has no font — from the stylesheet's own
 * numbers: a monospace face at 10px with 0.06em tracking is 6.6px an
 * upper-case character, 11px tall. An estimate a pixel or two wide is
 * safe; the rule is "clear by a margin", not "touching". The coupling to
 * the stylesheet is one-to-MANY, not one-to-one (Calder, measured on the
 * deployed map, 2026-09-12): every selector that sets its own tracking
 * is a row in {@link HOLO_CHAR_ADVANCE}, keyed by the label's {@link LabelType}
 * — an unknown room's `???` is spaced at 0.2em so it reads as uncertain
 * rather than as a word, and is 8px a character, not 6.6. The plan sets
 * its type a size larger and keeps its own table (D222), so the width
 * estimate takes the table as a parameter and this one is only its default.
 *
 * **The search.** Candidates in order of preference: on the top face
 * (only when the face's bounding box holds the text), beside the box to
 * the left, to the right, just above it on a leader, below it on a leader,
 * beside it at its foot, then above at rising steps, and finally on the
 * face regardless. The
 * first candidate that overlaps no other box's silhouette, no floor label
 * and no label already placed wins. Placement runs FRONT to back — nearest the viewer first, the
 * active room before all — so the labels a player reads first get the
 * best ground. Everything is deterministic: the same graph places the same
 * labels.
 *
 * @packageDocumentation
 */

/** A point on screen. */
export type Point = readonly [number, number];

/** Four corners of one face, in drawing order: top apex, right corner, bottom apex, left corner. */
export type Quad = readonly [Point, Point, Point, Point];

/** An axis-aligned rectangle on screen. */
export interface Rect {
	readonly bottom: number;
	readonly left: number;
	readonly right: number;
	readonly top: number;
}

/** How SVG anchors a text run to its `x`. */
export type LabelAnchor = 'end' | 'middle' | 'start';

/** A placed label: where its baseline starts, how it hangs from that point, and the leader when it floats. */
export interface PlacedLabel {
	readonly anchor: LabelAnchor;
	/** From the box's top apex to the label, when the label is above the box rather than on or beside it. */
	readonly leader?: { readonly x1: number; readonly x2: number; readonly y1: number; readonly y2: number };
	readonly x: number;
	readonly y: number;
}

/**
 * Which type a label is set in — one member per stylesheet selector that
 * sets its own tracking. `name`: a room's name (`.orpheus-holo__label`).
 * `unknown`: the mark of a room not yet known (`.orpheus-holo__node--unknown`),
 * spaced wider so it reads as uncertain rather than as a word.
 */
export type LabelType = 'name' | 'unknown';

/**
 * The size a label is set at: the drawing's own, or a size down (D224). On a
 * narrow figure the hologram, standing in for the scene, follows the scene's
 * answer to crowding (content standards §3.11): the walked rooms the player
 * is not in are set smaller rather than hidden — the scene's 8px against its
 * 10px, so the hologram's small is the same ratio of its own base.
 */
export type LabelSize = 'base' | 'small';

/** How much narrower a small label's advance is than its base: 8px against 10px. */
export const SMALL_LABEL_SCALE = 0.8;

/** A label's text, the type it is set in, and the size — what its width is estimated from. */
export interface LabelText {
	readonly label: string;
	/** Absent means the drawing's own size. */
	readonly size?: LabelSize;
	readonly type: LabelType;
}

/** One box to label: its base and top faces (the silhouette is their corners together), and the text. */
export interface LabelSubject extends LabelText {
	readonly base: Quad;
	readonly top: Quad;
}

/** How far one character advances the pen, in SVG units, for each type a label is set in. */
export type CharAdvance = Readonly<Record<LabelType, number>>;

/**
 * The stylesheet's type, as numbers: `.orpheus-holo__label` is 10px
 * monospace — 6px a character — with 0.06em tracking; an unknown room's
 * label carries 0.2em instead. One row per selector, so a tracking the
 * stylesheet sets is a tracking the estimate knows.
 */
export const HOLO_CHAR_ADVANCE: CharAdvance = { name: 6.6, unknown: 8 };
const ASCENT = 8;
const DESCENT = 3;
/** Clear air between a label and whatever it sits beside. */
const GAP = 6;
/** How far each successive "above" candidate climbs. */
const STEP = ASCENT + DESCENT + 2;
const ABOVE_STEPS = 5;
/** The baseline sits this far below a face's vertical center, so the text reads centered. */
const BASELINE_DROP = 3.5;

/** `.orpheus-holo__floor-label`: 9px with 0.22em tracking. */
const FLOOR_CHAR_ADVANCE = 7.4;
const FLOOR_ASCENT = 7;

/**
 * The width of a label as drawn, from the stylesheet's advance for its type.
 *
 * @param text - The label and the type it is set in
 * @param advance - The size table of the drawing it is set for; the hologram's unless told otherwise
 * @returns The width, in SVG units, rounded to the unit
 */
export const labelWidthOf = ({ label, size, type }: LabelText, advance: CharAdvance = HOLO_CHAR_ADVANCE): number =>
	Math.round([...label].length * advance[type] * (size === 'small' ? SMALL_LABEL_SCALE : 1));

/** The rectangle a floor plate's label covers — start-anchored, as the plates draw it. */
export const floorLabelRectOf = (label: string, x: number, baselineY: number): Rect => ({
	bottom: baselineY + DESCENT,
	left: x,
	right: x + Math.round([...label].length * FLOOR_CHAR_ADVANCE),
	top: baselineY - FLOOR_ASCENT,
});

/** The rectangle a label covers, from its anchor point and how it hangs. */
export const labelRectOf = (text: LabelText, x: number, baselineY: number, anchor: LabelAnchor): Rect => {
	const width = labelWidthOf(text);
	const left = anchor === 'start' ? x : anchor === 'end' ? x - width : x - width / 2;

	return { bottom: baselineY + DESCENT, left, right: left + width, top: baselineY - ASCENT };
};

/** The bounding box of some points. */
export const boundsOf = (points: readonly Point[]): Rect => ({
	bottom: Math.max(...points.map(([, y]) => y)),
	left: Math.min(...points.map(([x]) => x)),
	right: Math.max(...points.map(([x]) => x)),
	top: Math.min(...points.map(([, y]) => y)),
});

/** Whether two rectangles share any area. */
export const rectsOverlap = (a: Rect, b: Rect): boolean => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/** Whether `inner` lies wholly within `outer`. */
const rectWithin = (inner: Rect, outer: Rect): boolean =>
	inner.left >= outer.left && inner.right <= outer.right && inner.top >= outer.top && inner.bottom <= outer.bottom;

/** A candidate position, with whether it is acceptable only when the face holds the whole label. */
interface Candidate extends PlacedLabel {
	readonly mustFitFace?: boolean;
}

/**
 * The candidates for one box, in order of preference, and the one taken
 * when none is clear: the face regardless, so a drawing is never left
 * without a name.
 *
 * @param top - The top face
 * @returns The candidates, and the fallback
 */
const candidatesFor = (top: Quad, base: Quad): { readonly fallback: Candidate; readonly preferred: readonly Candidate[] } => {
	const [apex, rightCorner, , leftCorner] = top;
	const [, baseRight, bottom, baseLeft] = base;
	const centerX = (apex[0] + top[2][0]) / 2;
	const centerY = (apex[1] + top[2][1]) / 2;
	const onFace: Candidate = { anchor: 'middle', x: centerX, y: centerY + BASELINE_DROP };
	// Sliced rather than destructured: `[nearest, ...higher]` types `nearest`
	// as possibly absent even though the array is built to a fixed length, and
	// the `nearest === undefined ? [] : [nearest]` that follows from it is a
	// branch nothing can enter. `slice` says the same thing with no question in
	// it — the first step, then the rest.
	const above = Array.from({ length: ABOVE_STEPS }, (_, step): Candidate => {
		const y = apex[1] - GAP - step * STEP;

		return { anchor: 'middle', leader: { x1: centerX, x2: centerX, y1: apex[1], y2: y + DESCENT }, x: centerX, y };
	});
	// Under the box's front apex, on a leader down from it — the front row's open ground.
	const belowY = bottom[1] + GAP + ASCENT;
	const below: Candidate = { anchor: 'middle', leader: { x1: bottom[0], x2: bottom[0], y1: bottom[1], y2: belowY - ASCENT }, x: bottom[0], y: belowY };

	return {
		fallback: onFace,
		preferred: [
			{ ...onFace, mustFitFace: true },
			{ anchor: 'end', x: leftCorner[0] - GAP, y: leftCorner[1] + BASELINE_DROP },
			{ anchor: 'start', x: rightCorner[0] + GAP, y: rightCorner[1] + BASELINE_DROP },
			...above.slice(0, 1),
			below,
			// Beside the box at its foot — lower than a floor's label at the plate's edge, for a tall box on the front row.
			{ anchor: 'end', x: baseLeft[0] - GAP, y: baseLeft[1] + BASELINE_DROP },
			{ anchor: 'start', x: baseRight[0] + GAP, y: baseRight[1] + BASELINE_DROP },
			...above.slice(1),
		],
	};
};

/**
 * Place every label.
 *
 * @param subjects - The boxes to label, in the order they should be placed — the first gets the best ground
 * @param reserved - Rectangles already spoken for (the floor plates' labels)
 * @returns Each subject with its placement, in the subjects' order
 */
export const placeLabels = <Subject extends LabelSubject>(subjects: readonly Subject[], reserved: readonly Rect[]): readonly (readonly [Subject, PlacedLabel])[] => {
	const silhouettes = subjects.map((subject) => boundsOf([...subject.base, ...subject.top]));
	const taken: Rect[] = [...reserved];
	const placements: (readonly [Subject, PlacedLabel])[] = [];

	for (const [index, subject] of subjects.entries()) {
		const others = silhouettes.filter((_, otherIndex) => otherIndex !== index);
		const face = boundsOf(subject.top);
		const isClear = (candidate: Candidate): boolean => {
			const rect = labelRectOf(subject, candidate.x, candidate.y, candidate.anchor);

			if (candidate.mustFitFace === true && !rectWithin(rect, face)) {
				return false;
			}

			return ![...others, ...taken].some((obstacle) => rectsOverlap(rect, obstacle));
		};
		const { fallback, preferred } = candidatesFor(subject.top, subject.base);
		const chosen = preferred.find(isClear) ?? fallback;
		const placement: PlacedLabel = { anchor: chosen.anchor, ...(chosen.leader === undefined ? {} : { leader: chosen.leader }), x: chosen.x, y: chosen.y };

		taken.push(labelRectOf(subject, placement.x, placement.y, placement.anchor));
		placements.push([subject, placement]);
	}

	return placements;
};
